import React, { useRef } from 'react';

type Props = {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  hasError?: boolean;
};

const OtpInput = ({
  value,
  onChange,
  length = 6,
  disabled = false,
  hasError = false,
}: Props): JSX.Element => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const focusInput = (index: number): void => {
    const target = inputRefs.current[index];
    if (target) {
      target.focus();
      target.select();
    }
  };

  // Fixed-length view of the current value so every box maps to a known position.
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  const handleChange = (index: number, raw: string): void => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (!digit) {
      return;
    }
    const next = [...digits];
    next[index] = digit;
    onChange(next.join(''));
    if (index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...digits];
      if (next[index]) {
        next[index] = '';
        onChange(next.join(''));
      } else if (index > 0) {
        next[index - 1] = '';
        onChange(next.join(''));
        focusInput(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) {
      return;
    }
    onChange(pasted);
    focusInput(Math.min(pasted.length, length - 1));
  };

  return (
    <div className="flex flex-row gap-1 sm:gap-2 w-full">
      {digits.map((digit, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          disabled={disabled}
          value={digit}
          aria-label={`OTP digit ${index + 1}`}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          // Boxes flex to fill the full available width at every breakpoint;
          // a fixed height keeps them from stretching too tall on wide screens.
          className={`form-input flex-1 min-w-0 h-12 text-base sm:text-xl text-center border bg-black rounded-md text-white focus:border-blue-400 ${
            hasError ? 'border-red-400' : 'border-gray-200'
          }`}
        />
      ))}
    </div>
  );
};

export default OtpInput;
